import { FormFieldsData } from 'framework/form';
import {
  BillingChangeRequestItem,
  BillingFormData,
  BillingMilestone,
  BillingPeriodItems,
  BillingPeriodType,
  CalendarMonthPeriod,
  CustomPeriod,
} from './types';
import { cloneDeep } from 'lodash';
import {
  BUDGET_SOURCE,
  useProjectFinanceService,
  useNewProjectService,
  MilestoneType,
  SingleProjectType,
  BillingType,
  BudgetsType,
  BudgetsFinanceType,
  STRUCTURE_ITEM_STATUS,
} from 'domain/shared/newProject';
import { ERROR_MESSAGES, INVOICE_DATE_BUSINESS_RULE, PERIOD_POLICY, SCHEDULE_ITEM_TYPE } from './constant';
import { useParams, useSearchParams } from 'react-router-dom';
import { HTTP } from 'framework/api';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import {
  DEFAULT_MILESTONE_STRUCT,
  generateBillingPeriods,
  getExistingBillingScheduleItems,
  getBillingScheduleFormItems,
  isBillingScheduleItemFullyLocked,
  isBillingScheduleItemRemovable,
} from './helpers/projectBillingHelper';
import useMilestonesService, { MILESTONES_ALIAS } from '../milestones/useMilestonesService';
import { MilestonesType } from '../milestones/types';
import useBillingService from '../finance/billing/billingService';
import {
  CreateBillingScheduleRequest,
  ExistingScheduleItemUpdateRequest,
  ScheduleItemDto as BillingScheduleItemRequest,
  UpdateBillingScheduleRequest,
} from '../finance/billing/billingTypes';

const normalizeAmount = (amount?: string): number | undefined => {
  if (amount === undefined || amount === null || amount === '') {
    return undefined;
  }

  return Number(amount);
};

const getItemSowId = (item: BillingMilestone | CalendarMonthPeriod | CustomPeriod, data: BillingFormData) =>
  item.sowId ?? data.sowId;

const isChangeRequestScheduleItem = (item: BillingPeriodItems): item is BillingChangeRequestItem =>
  item.itemType === 'CHANGE_REQUEST';

const isEditableScheduleItem = (
  item: BillingPeriodItems,
): item is BillingMilestone | CalendarMonthPeriod | CustomPeriod => !isChangeRequestScheduleItem(item);

const mapScheduleItemToRequest = (
  item: BillingMilestone | CalendarMonthPeriod | CustomPeriod,
  data: BillingFormData,
  options?: {
    includeHours?: boolean;
  },
): BillingScheduleItemRequest => {
  const includeHours = options?.includeHours ?? true;
  const isMilestone = data.billingType === BillingType.MILESTONE;
  const periodPolicy = data.periodPolicy;
  if (!isMilestone) {
    if (periodPolicy === PERIOD_POLICY.CALENDAR_MONTH) {
      const calendarData = item as CalendarMonthPeriod;
      return {
        ...(calendarData.id ? { id: calendarData.id } : {}),
        type: SCHEDULE_ITEM_TYPE.CALENDAR_PERIOD as 'CALENDAR_PERIOD',
        sowId: getItemSowId(calendarData, data),
        periodMonth: calendarData.periodMonth,
        amount: normalizeAmount(calendarData.amount)!,
        currency: data.currency,
        ...(includeHours && calendarData.hours != null ? { hours: calendarData.hours } : {}),
      };
    }

    if (periodPolicy === PERIOD_POLICY.CUSTOM_PERIOD) {
      const customData = item as CustomPeriod;
      return {
        ...(customData.id ? { id: customData.id } : {}),
        type: SCHEDULE_ITEM_TYPE.CUSTOM_PERIOD as 'CUSTOM_PERIOD',
        sowId: getItemSowId(customData, data),
        invoiceDate: customData.invoiceDate,
        startDate: customData.startDate,
        endDate: customData.endDate,
        amount: normalizeAmount(customData.amount)!,
        currency: data.currency,
        ...(includeHours && customData.hours != null ? { hours: customData.hours } : {}),
      };
    }
  }

  const milestoneData = item as BillingMilestone;
  return {
    ...(milestoneData.id ? { id: milestoneData.id } : {}),
    type: SCHEDULE_ITEM_TYPE.MILESTONE as 'MILESTONE',
    sowId: getItemSowId(milestoneData, data),
    milestoneType: milestoneData.milestoneType as MilestoneType,
    milestoneName: milestoneData.milestoneName,
    amount: normalizeAmount(milestoneData.amount)!,
    currency: data.currency,
    ...(milestoneData.alias ? { alias: milestoneData.alias } : {}),
    ...(milestoneData.invoiceDate ? { invoiceDate: milestoneData.invoiceDate } : {}),
    ...(milestoneData.isCompleted != null ? { isCompleted: milestoneData.isCompleted } : {}),
    ...(includeHours && milestoneData.hours != null ? { hours: milestoneData.hours } : {}),
  };
};

export const mapFormData = (data: BillingFormData): CreateBillingScheduleRequest => {
  const isMilestone = data.billingType === BillingType.MILESTONE;
  const periodPolicy = data.periodPolicy;
  const shouldSendInvoiceDateBusinessRule = !isMilestone && periodPolicy === PERIOD_POLICY.CALENDAR_MONTH;

  const payload: CreateBillingScheduleRequest = {
    requestId: data.requestId,
    projectId: data.projectId,
    accountId: data.accountId,
    billingType: data.billingType,
    periodPolicy: !isMilestone ? periodPolicy : undefined,
    sowStartDate: data.startDate,
    sowEndDate: data.endDate,
    scheduleItems: data.scheduleItems.filter(isEditableScheduleItem).map(item => mapScheduleItemToRequest(item, data)),
  };

  if (shouldSendInvoiceDateBusinessRule) {
    return {
      ...payload,
      invoiceDateBusinessRule: data.invoiceDateBusinessRule ?? INVOICE_DATE_BUSINESS_RULE.BY_1ST_BUSINESS_DAY,
    };
  }

  return payload;
};

export const mapUpdateFormData = (data: BillingFormData): UpdateBillingScheduleRequest => {
  const currentItemIds = new Set(data.scheduleItems.filter(item => Boolean(item.id)).map(item => item.id!));
  const existingItems: ExistingScheduleItemUpdateRequest[] = data.scheduleItems
    .filter(
      (item): item is BillingMilestone | CalendarMonthPeriod | CustomPeriod =>
        Boolean(item.id) && isEditableScheduleItem(item) && !isBillingScheduleItemFullyLocked(item),
    )
    .map(item => {
      const payload: ExistingScheduleItemUpdateRequest = {
        id: item.id!,
        sowId: getItemSowId(item, data),
        amount: normalizeAmount(item.amount),
        currency: data.currency,
      };

      if ('startDate' in item && 'endDate' in item) {
        payload.invoiceDate = item.invoiceDate || null;
      }

      if ('milestoneName' in item) {
        payload.milestoneName = item.milestoneName;
      }

      return payload;
    });

  const newItems = data.scheduleItems
    .filter(
      (item): item is BillingMilestone | CalendarMonthPeriod | CustomPeriod => !item.id && isEditableScheduleItem(item),
    )
    .map(item => mapScheduleItemToRequest(item, data, { includeHours: false }));

  const removedItemIds = (data.originalScheduleItems ?? [])
    .filter(item => Boolean(item.id) && isBillingScheduleItemRemovable(item) && !currentItemIds.has(item.id!))
    .map(item => item.id!);

  return {
    existingItems,
    newItems,
    removedItemIds,
  };
};

const getActiveStructure = (isMilestone: boolean, budgetMain: BudgetsType, budgetFinance: BudgetsFinanceType) => {
  let activeStructure = null;

  if (isMilestone) {
    // For milestone: only common structures
    const budgetIds = new Set(budgetMain?.structure?.map(s => s.id) || []);
    activeStructure = budgetFinance?.structure?.find(
      item => budgetIds.has(item.id) && item.status === STRUCTURE_ITEM_STATUS.APPROVED,
    );
  } else {
    // For periodical budgets: any financial structure
    activeStructure = budgetFinance?.structure?.find(
      item => item.status === STRUCTURE_ITEM_STATUS.APPROVED && item.source === BUDGET_SOURCE.SOW,
    );
  }

  return activeStructure;
};

const getSowStructures = (budgetMain?: BudgetsType, budgetFinance?: BudgetsFinanceType) => {
  const mainStructures = budgetMain?.structure?.filter(item => item.source === BUDGET_SOURCE.SOW) ?? [];

  if (mainStructures.length > 0) {
    return mainStructures;
  }

  return budgetFinance?.structure?.filter(item => item.source === BUDGET_SOURCE.SOW) ?? [];
};

export const getScheduleStartDate = ({
  activeStructure,
  budgetFinance,
  budgetMain,
  hasExistingBillingSchedule,
}: {
  activeStructure?: { effectiveStartDate?: string | null } | null;
  budgetFinance?: BudgetsFinanceType;
  budgetMain?: BudgetsType;
  hasExistingBillingSchedule: boolean;
}): string | undefined => {
  if (!hasExistingBillingSchedule) {
    return activeStructure?.effectiveStartDate ?? undefined;
  }

  const earliestSowStartDate = getSowStructures(budgetMain, budgetFinance)
    .map(item => item.effectiveStartDate)
    .filter(Boolean)
    .sort()[0];

  return earliestSowStartDate ?? activeStructure?.effectiveStartDate ?? undefined;
};

const useProjectBillingService = () => {
  const { getById } = useNewProjectService();
  const { getBudget, budgetsFinanceSearch } = useProjectFinanceService();
  const { billingScheduleSearch, billingScheduleSensitiveSearch, createBillingSchedule, updateBillingSchedule } =
    useBillingService();
  const { id, requestId: requestIdFromParams } = useParams();
  const [searchParams] = useSearchParams();
  const { addMessage } = useMessageService();
  const { milestonesSearch } = useMilestonesService();
  const requestId = requestIdFromParams ?? searchParams.get('requestId');
  const getFormData = async (): Promise<BillingFormData | undefined> => {
    if (!id) {
      addMessage(new ErrorMessage(ERROR_MESSAGES.PROJECT_ID_REQUIRED));
      return undefined;
    }

    try {
      const projectData = (await getById(id)) as SingleProjectType;

      if (!projectData?.defaultBudgetId) {
        addMessage(new ErrorMessage(ERROR_MESSAGES.BUDGET_NOT_FOUND));
        return undefined;
      }

      const [milestonesResponse, budgetResponse, financeResponse, existingScheduleResponse] = await Promise.all([
        milestonesSearch(id!, {
          filter: {
            deletedAt: {
              null: true,
            },
          },
        }),
        getBudget(projectData.defaultBudgetId).catch((e: Response) => {
          if (e.status === HTTP.NOT_FOUND) {
            addMessage(new ErrorMessage(ERROR_MESSAGES.BUDGET_NOT_FOUND));
            return undefined;
          }
          throw e;
        }),
        budgetsFinanceSearch({
          page: 0,
          size: 1,
          sort: { createdAt: 'ASC' },
          filter: { id: { eq: projectData.defaultBudgetId } },
        }).then(response => response?.result?.[0]),
        billingScheduleSearch({
          pageSize: 1,
          filter: {
            projectId: {
              eq: id,
            },
          },
        }).then(response => response?.result?.[0]),
      ]);

      if (!budgetResponse || !financeResponse) {
        addMessage(new ErrorMessage(ERROR_MESSAGES.FAILED_LOAD_BILLING));
        return undefined;
      }

      const milestones: MilestonesType[] = milestonesResponse?.result || [];
      const billingMilestones: BillingMilestone[] = milestones
        .filter(milestone => milestone.type !== MILESTONES_ALIAS.NON_BILLING_MILESTONE)
        .map(milestone => {
          const invoiceDate =
            milestone.completionDate ?? milestone.currentPlannedDate ?? milestone.initialPlannedDate ?? null;
          return {
            milestoneName: milestone.title || milestone.alias || '',
            milestoneType: milestone.type || '',
            alias: milestone.alias,
            invoiceDate: invoiceDate,
            isCompleted: !!milestone.completionDate,
          };
        });

      const billingType = (budgetResponse?.billingType || financeResponse?.billingType) as BillingType;
      const isMilestone = billingType === BillingType.MILESTONE;
      const existingSchedule =
        existingScheduleResponse && existingScheduleResponse.billingType === billingType
          ? existingScheduleResponse
          : undefined;
      const sensitiveScheduleResponse = existingSchedule
        ? await billingScheduleSensitiveSearch({
            pageNumber: 0,
            pageSize: 100,
            filter: {
              id: {
                eq: existingSchedule.id,
              },
            },
          }).catch(() => null)
        : null;
      const periodPolicy =
        (existingSchedule?.periodPolicy as BillingPeriodType | null) ??
        (PERIOD_POLICY.CALENDAR_MONTH as BillingPeriodType);

      const activeStructure = getActiveStructure(isMilestone, budgetResponse, financeResponse);

      if (!activeStructure) {
        addMessage(new ErrorMessage(ERROR_MESSAGES.FAILED_LOAD_BILLING));
        return undefined;
      }

      const scheduleStartDate = getScheduleStartDate({
        activeStructure,
        budgetFinance: financeResponse,
        budgetMain: budgetResponse,
        hasExistingBillingSchedule: Boolean(existingSchedule),
      });
      const scheduleEndDate = activeStructure?.effectiveEndDate;

      if (!scheduleStartDate || !scheduleEndDate) {
        addMessage(new ErrorMessage(ERROR_MESSAGES.FAILED_LOAD_BILLING));
        return undefined;
      }

      const generatedScheduleItems = isMilestone
        ? billingMilestones.length > 0
          ? billingMilestones
          : DEFAULT_MILESTONE_STRUCT
        : generateBillingPeriods(periodPolicy, scheduleStartDate, scheduleEndDate);

      const sensitiveItemsMap = new Map(
        (sensitiveScheduleResponse?.result?.[0]?.scheduleItems ?? []).map((item: any) => [item.id, item]),
      );
      const originalScheduleItems = existingSchedule
        ? getExistingBillingScheduleItems(existingSchedule, sensitiveItemsMap)
        : undefined;

      const scheduleItems = getBillingScheduleFormItems({
        currentSowId: budgetResponse?.sowId,
        currentSowStartDate: activeStructure?.effectiveStartDate ?? undefined,
        generatedItems: generatedScheduleItems,
        schedule: existingSchedule,
        sensitiveItemsMap,
      });

      return {
        requestId: requestId || null,
        projectName: projectData.name || '',
        amount: activeStructure?.amount || null,
        currency: activeStructure?.currency || null,
        startDate: scheduleStartDate,
        endDate: scheduleEndDate,
        relatedDocuments: activeStructure.relatedDocuments,
        budgetId: projectData.defaultBudgetId,
        projectId: projectData.id,
        accountId: projectData.accountId,
        sowId: budgetResponse?.sowId,
        billingType,
        periodPolicy,
        invoiceDateBusinessRule: INVOICE_DATE_BUSINESS_RULE.BY_1ST_BUSINESS_DAY,
        billingScheduleId: existingSchedule?.id ?? null,
        hasExistingBillingSchedule: Boolean(existingSchedule),
        scheduleItems,
        originalScheduleItems: cloneDeep(originalScheduleItems ?? scheduleItems),
      };
    } catch (error) {
      addMessage(new ErrorMessage(ERROR_MESSAGES.FAILED_LOAD_BILLING));
      return undefined;
    }
  };

  const billingSchedule = async (data: FormFieldsData): Promise<void> => {
    const requestData = cloneDeep(data) as BillingFormData;
    if (requestData.billingScheduleId) {
      await updateBillingSchedule(requestData.billingScheduleId, mapUpdateFormData(requestData));
      return;
    }

    await createBillingSchedule(mapFormData(requestData));
  };

  return {
    getFormData,
    billingSchedule,
  };
};

export default useProjectBillingService;
